import type { Release } from '@central/api';
import { Card, PageHeader, formatDate } from '@central/ui';
import { Button, Code, CopyButton, Table } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { keys, useApi } from '../../api';
import { DataTable, Empty, QueryState } from '../../components/parts';
import { mono, muted } from '../../lib/labels';

/** Releases and their installers, newest first, for installing and upgrading GoATMS. */
export function PartnerReleases() {
  const api = useApi();
  const list = useQuery({
    queryKey: keys.partnerReleases,
    queryFn: () => api.partner.releases(),
  });
  return (
    <>
      <PageHeader
        title="Releases & installers"
        subtitle="Download the GoATMS installer for a new install or an upgrade"
      />
      <Card padded={false}>
        <QueryState isPending={list.isPending} error={list.error}>
          {() =>
            list.data!.items.length === 0 ? (
              <Empty>No releases yet.</Empty>
            ) : (
              <DataTable
                label="Releases"
                minWidth={720}
                head={['Version', 'Published', 'Highlights', 'Installer']}
              >
                {list.data!.items.map((r) => (
                  <Table.Tr key={r.id}>
                    <Table.Td style={{ ...mono, fontWeight: 500 }}>{r.version}</Table.Td>
                    <Table.Td>{formatDate(r.publishedAt)}</Table.Td>
                    <Table.Td style={{ fontSize: 13, maxWidth: 360 }}>
                      {r.highlights ?? <span style={muted}>–</span>}
                      {r.notesUrl && (
                        <div>
                          <a href={r.notesUrl} target="_blank" rel="noreferrer">
                            Release notes
                          </a>
                        </div>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {r.installerUrl ? (
                        <InstallerLink r={r} />
                      ) : (
                        <span style={muted}>Not yet</span>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </DataTable>
            )
          }
        </QueryState>
      </Card>
    </>
  );
}

/** Download button and, when known, the SHA-256 to check the download against. */
export function InstallerLink({ r }: { r: Release }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }}>
      <Button component="a" href={r.installerUrl} rel="noreferrer" color="dark" size="xs">
        Download {r.version}
      </Button>
      {r.installerSha256 && (
        <>
          <Code style={{ ...mono, fontSize: 11, wordBreak: 'break-all' }}>
            SHA-256 {r.installerSha256}
          </Code>
          <CopyButton value={r.installerSha256}>
            {({ copied, copy }) => (
              <Button variant="subtle" size="compact-xs" onClick={copy}>
                {copied ? 'Copied' : 'Copy checksum'}
              </Button>
            )}
          </CopyButton>
        </>
      )}
    </div>
  );
}
